// Certificado de conclusão do Ensino (16/10/2026).
// O registro do certificado (tr_dados.certificados) guarda os dados do momento
// da emissão; o PDF é desenhado na hora com jsPDF, sempre igual para o mesmo
// registro. Certificado anexado (PDF de fora) usa o bucket ensino-materiais.

import { jsPDF } from 'jspdf';
import logoKzero from '../assets/kzero-logo-color.png';

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

export function dataPorExtenso(iso) {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return `${d} de ${MESES[m - 1]} de ${a}`;
}

function dataCurta(iso) {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

// Código curto e estável a partir do id do registro, para conferência.
export function codigoCertificado(id) {
  const n = Math.abs(Math.round(Number(String(id).replace(/\D/g, '').slice(-12)) || Date.now()));
  return `KZ-${n.toString(36).toUpperCase().padStart(8, '0').slice(-8)}`;
}

/**
 * Aproveitamento do aluno na turma: a Prova final, se ele fez; senão a média
 * dos testes que fez. Retorna { percentual, base } ou null se não fez nenhum.
 */
export function aproveitamentoNaTurma(alunoId, turma, testes, respostas) {
  const testesDaTurma = testes.filter(t => String(t.turmaId) === String(turma.id));
  const resp = testesDaTurma
    .map(t => ({ t, r: respostas.find(r => r.testeId === t.id && String(r.alunoId) === String(alunoId)) }))
    .filter(x => x.r);
  if (!resp.length) return null;
  const final = resp.find(x => /^prova final/i.test(x.t.nome || ''));
  if (final) return { percentual: Math.round(final.r.percentual), base: 'Prova final' };
  const media = resp.reduce((s, x) => s + (x.r.percentual || 0), 0) / resp.length;
  return { percentual: Math.round(media), base: `média de ${resp.length} teste(s)` };
}

async function imagemComoDataUrl(src) {
  const blob = await fetch(src).then(r => r.blob());
  return new Promise((ok, erro) => {
    const fr = new FileReader();
    fr.onload = () => ok(fr.result);
    fr.onerror = erro;
    fr.readAsDataURL(blob);
  });
}

// Coloca uma imagem (data URL) centrada em cx, com largura máxima e altura máxima.
function imagemCentrada(doc, img, cx, yBase, maxW, maxH) {
  const p = doc.getImageProperties(img);
  let w = maxW, h = (p.height / p.width) * w;
  if (h > maxH) { h = maxH; w = (p.width / p.height) * h; }
  doc.addImage(img, p.fileType || 'PNG', cx - w / 2, yBase - h, w, h);
}

// Texto com letras espaçadas e centrado de verdade (o charSpace do jsPDF desloca o centro).
function textoEspacado(doc, texto, cx, y, espaco) {
  const letras = [...texto];
  const larguras = letras.map(l => doc.getTextWidth(l));
  const total = larguras.reduce((s, w) => s + w, 0) + espaco * (letras.length - 1);
  let x = cx - total / 2;
  letras.forEach((l, i) => { doc.text(l, x, y); x += larguras[i] + espaco; });
}

/**
 * Desenha o certificado e devolve o jsPDF (A4 deitado).
 * cfg (opcional): { assinaturaKzero, assinanteKzero, cargoKzero, assinaturasInstrutor: { [nome]: dataUrl } }
 * Sem imagem de assinatura da Kzero, o logo faz a assinatura institucional.
 */
export async function desenharCertificado(c, cfg = {}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = 297, H = 210;
  const roxoEsc = [46, 16, 101], roxo = [91, 33, 182], lilas = [237, 230, 251], dourado = [184, 146, 58];
  const escuro = [28, 21, 48], cinza = [95, 88, 116];

  let logo = null;
  try { logo = await imagemComoDataUrl(logoKzero); } catch { /* sem logo */ }

  // Fundo e faixa lateral
  doc.setFillColor(252, 251, 255); doc.rect(0, 0, W, H, 'F');
  doc.setFillColor(...roxoEsc); doc.rect(0, 0, 16, H, 'F');
  doc.setFillColor(...roxo); doc.rect(16, 0, 3, H, 'F');
  doc.setFillColor(...dourado); doc.rect(19, 0, 0.8, H, 'F');
  // Cantos decorativos à direita
  doc.setFillColor(...lilas); doc.triangle(W, 0, W - 46, 0, W, 46, 'F');
  doc.setFillColor(...roxo); doc.triangle(W, 0, W - 26, 0, W, 26, 'F');
  doc.setFillColor(...lilas); doc.triangle(W, H, W - 46, H, W, H - 46, 'F');
  doc.setFillColor(...roxoEsc); doc.triangle(W, H, W - 26, H, W, H - 26, 'F');
  // Moldura fina
  doc.setDrawColor(...dourado); doc.setLineWidth(0.35); doc.rect(27, 9, W - 36, H - 18);

  const cx = (27 + W - 9) / 2; // centro da área útil (à direita da faixa)

  if (logo) imagemCentrada(doc, logo, cx, 36, 46, 18);

  doc.setTextColor(...roxoEsc); doc.setFont('helvetica', 'bold'); doc.setFontSize(34);
  textoEspacado(doc, 'CERTIFICADO', cx, 54, 2.2);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(...dourado);
  textoEspacado(doc, 'DE CONCLUSÃO DE CURSO', cx, 62, 1.6);
  doc.setDrawColor(...dourado); doc.setLineWidth(0.5); doc.line(cx - 22, 66, cx + 22, 66);

  doc.setFontSize(12.5); doc.setTextColor(...cinza);
  doc.text('Certificamos que', cx, 80, { align: 'center' });

  doc.setFont('times', 'bolditalic'); doc.setFontSize(32); doc.setTextColor(...roxoEsc);
  doc.text(c.alunoNome || '', cx, 96, { align: 'center' });
  doc.setDrawColor(...roxo); doc.setLineWidth(0.3); doc.line(cx - 85, 100, cx + 85, 100);

  const periodo = c.dataInicio && c.dataFim ? `, realizado de ${dataCurta(c.dataInicio)} a ${dataCurta(c.dataFim)}` : '';
  const carga = c.cargaHoraria ? `, com carga horária de ${String(c.cargaHoraria).replace(/h$/i, '')} horas` : '';
  const nota = c.percentual != null ? `, com aproveitamento de ${c.percentual}%` : '';
  const frase = `concluiu o curso ${c.cursoNome}${carga}${periodo}${nota}.`;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(13); doc.setTextColor(...escuro);
  doc.text(doc.splitTextToSize(frase, 200), cx, 112, { align: 'center', lineHeightFactor: 1.55 });

  doc.setFontSize(10.5); doc.setTextColor(...cinza);
  doc.text(`Emitido em ${dataPorExtenso(c.emitidoEm)}`, cx, 138, { align: 'center' });

  // Assinaturas
  const y = 170, xEsq = cx - 72, xDir = cx + 72;
  const assInstrutor = cfg.assinaturasInstrutor?.[c.instrutorNome];
  try { if (assInstrutor) imagemCentrada(doc, assInstrutor, xEsq, y - 1.5, 58, 20); } catch { /* imagem inválida */ }
  // Duas assinaturas: instrutor(a) e aluno(a). O aluno assina à mão no impresso.
  doc.setDrawColor(...cinza); doc.setLineWidth(0.3);
  doc.line(xEsq - 36, y, xEsq + 36, y); doc.line(xDir - 36, y, xDir + 36, y);
  doc.setFontSize(10.5); doc.setTextColor(...escuro); doc.setFont('helvetica', 'bold');
  doc.text(c.instrutorNome || 'Instrutor(a)', xEsq, y + 5.5, { align: 'center' });
  doc.text(c.alunoNome || 'Aluno(a)', xDir, y + 5.5, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...cinza);
  doc.text('Instrutor(a)', xEsq, y + 10, { align: 'center' });
  doc.text('Aluno(a)', xDir, y + 10, { align: 'center' });

  doc.setFontSize(7.5); doc.setTextColor(...cinza);
  doc.text(`Código de verificação: ${c.codigo}`, cx, H - 13, { align: 'center' });
  return doc;
}

export async function baixarCertificado(c, cfg = {}) {
  const doc = await desenharCertificado(c, cfg);
  const nome = `Certificado - ${c.alunoNome} - ${c.cursoNome}`.replace(/[\\/:*?"<>|]/g, '-');
  doc.save(`${nome}.pdf`);
}
